import React, { useEffect } from 'react';

const WebSocketsLongPolling = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'WebSockets vs Long Polling - Real-Time Communication Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master WebSockets and Long Polling for real-time web applications. Compare protocols, implementation patterns, and scalability considerations.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master WebSockets and Long Polling for real-time web applications. Compare protocols, implementation patterns, and scalability considerations.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>WebSockets vs Long Polling: Real-Time Communication</h1>
        <p style={styles.subtitle}>
          Build responsive real-time applications with WebSockets and Long Polling techniques
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Real-Time Communication</h2>
          <p style={styles.paragraph}>
            Real-time communication enables instant data exchange between clients and servers, creating 
            responsive user experiences for applications like chat systems, live dashboards, gaming, 
            and collaborative tools. Traditional HTTP request-response patterns aren't sufficient for 
            real-time needs, leading to specialized techniques like WebSockets and Long Polling.
          </p>
          <p style={styles.paragraph}>
            Both WebSockets and Long Polling solve the fundamental problem of server-initiated communication, 
            but they use different approaches with distinct trade-offs in terms of performance, complexity, 
            and resource usage.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>WebSockets Overview</h2>
          
          <h3 style={styles.heading}>What are WebSockets?</h3>
          <p style={styles.paragraph}>
            WebSockets provide a persistent, full-duplex communication channel between client and server 
            over a single TCP connection. After the initial HTTP handshake, the connection is upgraded 
            to WebSocket protocol, allowing both parties to send data at any time without the overhead 
            of HTTP headers.
          </p>
          <pre style={styles.codeBlock}>
{`WebSocket Protocol Flow:

1. HTTP Handshake:
   Client Request:
   GET /chat HTTP/1.1
   Host: example.com
   Upgrade: websocket
   Connection: Upgrade
   Sec-WebSocket-Key: dGhlIHNhbXBsZSBub25jZQ==
   Sec-WebSocket-Version: 13

   Server Response:
   HTTP/1.1 101 Switching Protocols
   Upgrade: websocket
   Connection: Upgrade
   Sec-WebSocket-Accept: s3pPLMBiTxaQ9kYGzzhZRbK+xOo=

2. WebSocket Communication:
   - Bidirectional message exchange
   - Low overhead frames
   - No HTTP headers per message
   - Persistent connection until closed

Benefits:
✅ Low latency communication
✅ Bidirectional data flow
✅ Minimal protocol overhead
✅ Excellent for high-frequency updates
✅ Built-in browser support

Challenges:
❌ Connection state management
❌ Load balancer complexity
❌ Firewall/proxy issues
❌ Resource consumption for idle connections
❌ Reconnection logic required`}
          </pre>

          <h3 style={styles.heading}>WebSocket Implementation</h3>
          <p style={styles.paragraph}>
            Implementing WebSockets requires careful handling of connection lifecycle, message routing, 
            and error recovery to build robust real-time applications.
          </p>
          <pre style={styles.codeBlock}>
{`WebSocket Server Implementation (Node.js):

const WebSocket = require('ws');
const http = require('http');

class WebSocketServer {
    constructor(port) {
        this.port = port;
        this.clients = new Map();
        this.rooms = new Map();
        this.server = null;
        this.wss = null;
    }

    start() {
        // Create HTTP server
        this.server = http.createServer();
        
        // Create WebSocket server
        this.wss = new WebSocket.Server({ 
            server: this.server,
            perMessageDeflate: false // Disable compression for simplicity
        });

        this.wss.on('connection', (ws, request) => {
            this.handleConnection(ws, request);
        });

        this.server.listen(this.port, () => {
        });
    }

    handleConnection(ws, request) {
        const clientId = this.generateClientId();
        const clientInfo = {
            id: clientId,
            ws: ws,
            rooms: new Set(),
            lastPing: Date.now(),
            metadata: {}
        };

        this.clients.set(clientId, clientInfo);

        // Send welcome message
        this.sendToClient(clientId, {
            type: 'connection',
            clientId: clientId,
            message: 'Connected successfully'
        });

        // Handle messages
        ws.on('message', (data) => {
            this.handleMessage(clientId, data);
        });

        // Handle disconnection
        ws.on('close', (code, reason) => {
            this.handleDisconnection(clientId, code, reason);
        });

        // Handle errors
        ws.on('error', (error) => {
            console.error(\`Client $\{clientId\} error:\`, error);
            this.handleDisconnection(clientId, 1011, 'Server error');
        });

        // Setup ping/pong for connection health
        ws.on('pong', () => {
            if (this.clients.has(clientId)) {
                this.clients.get(clientId).lastPing = Date.now();
            }
        });

        // Start heartbeat
        this.startHeartbeat(clientId);
    }

    handleMessage(clientId, data) {
        try {
            const message = JSON.parse(data);
            const client = this.clients.get(clientId);

            if (!client) return;

            switch (message.type) {
                case 'join_room':
                    this.joinRoom(clientId, message.room);
                    break;
                
                case 'leave_room':
                    this.leaveRoom(clientId, message.room);
                    break;
                
                case 'chat_message':
                    this.broadcastToRoom(message.room, {
                        type: 'chat_message',
                        clientId: clientId,
                        message: message.content,
                        timestamp: Date.now()
                    }, clientId);
                    break;
                
                case 'private_message':
                    this.sendToClient(message.targetClientId, {
                        type: 'private_message',
                        fromClientId: clientId,
                        message: message.content,
                        timestamp: Date.now()
                    });
                    break;
                
                case 'ping':
                    this.sendToClient(clientId, { type: 'pong' });
                    break;
                
                default:
            }
        } catch (error) {
            console.error(\`Error parsing message from $\{clientId\}:\`, error);
            this.sendToClient(clientId, {
                type: 'error',
                message: 'Invalid message format'
            });
        }
    }

    joinRoom(clientId, roomName) {
        const client = this.clients.get(clientId);
        if (!client) return;

        // Add client to room
        if (!this.rooms.has(roomName)) {
            this.rooms.set(roomName, new Set());
        }
        
        this.rooms.get(roomName).add(clientId);
        client.rooms.add(roomName);

        // Notify client
        this.sendToClient(clientId, {
            type: 'room_joined',
            room: roomName,
            memberCount: this.rooms.get(roomName).size
        });

        // Notify other room members
        this.broadcastToRoom(roomName, {
            type: 'user_joined',
            clientId: clientId,
            room: roomName
        }, clientId);
    }

    leaveRoom(clientId, roomName) {
        const client = this.clients.get(clientId);
        if (!client || !this.rooms.has(roomName)) return;

        this.rooms.get(roomName).delete(clientId);
        client.rooms.delete(roomName);

        // Clean up empty rooms
        if (this.rooms.get(roomName).size === 0) {
            this.rooms.delete(roomName);
        }

        // Notify client
        this.sendToClient(clientId, {
            type: 'room_left',
            room: roomName
        });

        // Notify remaining room members
        if (this.rooms.has(roomName)) {
            this.broadcastToRoom(roomName, {
                type: 'user_left',
                clientId: clientId,
                room: roomName
            });
        }
    }

    sendToClient(clientId, message) {
        const client = this.clients.get(clientId);
        if (!client || client.ws.readyState !== WebSocket.OPEN) {
            return false;
        }

        try {
            client.ws.send(JSON.stringify(message));
            return true;
        } catch (error) {
            console.error(\`Error sending to client \${clientId}:\`, error);
            return false;
        }
    }

    broadcastToRoom(roomName, message, excludeClientId = null) {
        const room = this.rooms.get(roomName);
        if (!room) return;

        let sentCount = 0;
        for (const clientId of room) {
            if (clientId !== excludeClientId) {
                if (this.sendToClient(clientId, message)) {
                    sentCount++;
                }
            }
        }
        return sentCount;
    }

    broadcastToAll(message, excludeClientId = null) {
        let sentCount = 0;
        for (const [clientId, client] of this.clients) {
            if (clientId !== excludeClientId) {
                if (this.sendToClient(clientId, message)) {
                    sentCount++;
                }
            }
        }
        return sentCount;
    }

    startHeartbeat(clientId) {
        const heartbeat = setInterval(() => {
            const client = this.clients.get(clientId);
            if (!client) {
                clearInterval(heartbeat);
                return;
            }

            if (Date.now() - client.lastPing > 60000) { // 60 seconds timeout
                client.ws.terminate();
                clearInterval(heartbeat);
                return;
            }

            if (client.ws.readyState === WebSocket.OPEN) {
                client.ws.ping();
            }
        }, 30000); // Ping every 30 seconds
    }

    handleDisconnection(clientId, code, reason) {
        const client = this.clients.get(clientId);
        if (!client) return;


        // Remove from all rooms
        for (const roomName of client.rooms) {
            this.leaveRoom(clientId, roomName);
        }

        // Remove client
        this.clients.delete(clientId);
    }

    generateClientId() {
        return 'client_' + Math.random().toString(36).substr(2, 9);
    }

    getStats() {
        return {
            connectedClients: this.clients.size,
            activeRooms: this.rooms.size,
            totalRoomMembers: Array.from(this.rooms.values())
                .reduce((sum, room) => sum + room.size, 0)
        };
    }
}

// Start server
const wsServer = new WebSocketServer(8080);
wsServer.start();

// Periodic stats logging
setInterval(() => {
}, 60000);`}
          </pre>

          <h3 style={styles.heading}>WebSocket Client Implementation</h3>
          <p style={styles.paragraph}>
            WebSocket clients need robust connection management, automatic reconnection, and message queuing 
            to handle network interruptions gracefully.
          </p>
          <pre style={styles.codeBlock}>
{`WebSocket Client Implementation (JavaScript):

class WebSocketClient {
    constructor(url, options = {}) {
        this.url = url;
        this.options = {
            reconnectInterval: 5000,
            maxReconnectAttempts: 10,
            heartbeatInterval: 30000,
            ...options
        };
        
        this.ws = null;
        this.clientId = null;
        this.reconnectAttempts = 0;
        this.isConnected = false;
        this.messageQueue = [];
        this.eventHandlers = new Map();
        this.heartbeatTimer = null;
        this.reconnectTimer = null;
    }

    connect() {
        try {
            this.ws = new WebSocket(this.url);
            this.setupEventHandlers();
        } catch (error) {
            console.error('WebSocket connection error:', error);
            this.scheduleReconnect();
        }
    }

    setupEventHandlers() {
        this.ws.onopen = (event) => {
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.startHeartbeat();
            this.processMessageQueue();
            this.emit('connected', event);
        };

        this.ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                this.handleMessage(message);
            } catch (error) {
                console.error('Error parsing message:', error);
            }
        };

        this.ws.onclose = (event) => {
            this.isConnected = false;
            this.stopHeartbeat();
            this.emit('disconnected', event);
            
            if (!event.wasClean && this.reconnectAttempts < this.options.maxReconnectAttempts) {
                this.scheduleReconnect();
            }
        };

        this.ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            this.emit('error', error);
        };
    }

    handleMessage(message) {
        switch (message.type) {
            case 'connection':
                this.clientId = message.clientId;
                break;
            
            case 'pong':
                // Heartbeat response received
                break;
            
            case 'chat_message':
                this.emit('chatMessage', message);
                break;
            
            case 'private_message':
                this.emit('privateMessage', message);
                break;
            
            case 'room_joined':
                this.emit('roomJoined', message);
                break;
            
            case 'user_joined':
                this.emit('userJoined', message);
                break;
            
            case 'error':
                this.emit('error', new Error(message.message));
                break;
            
            default:
                this.emit('message', message);
        }
    }

    send(message) {
        if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
            return true;
        } else {
            // Queue message for later sending
            this.messageQueue.push(message);
            return false;
        }
    }

    sendChatMessage(room, content) {
        return this.send({
            type: 'chat_message',
            room: room,
            content: content
        });
    }

    sendPrivateMessage(targetClientId, content) {
        return this.send({
            type: 'private_message',
            targetClientId: targetClientId,
            content: content
        });
    }

    joinRoom(roomName) {
        return this.send({
            type: 'join_room',
            room: roomName
        });
    }

    leaveRoom(roomName) {
        return this.send({
            type: 'leave_room',
            room: roomName
        });
    }

    processMessageQueue() {
        while (this.messageQueue.length > 0 && this.isConnected) {
            const message = this.messageQueue.shift();
            this.send(message);
        }
    }

    startHeartbeat() {
        this.heartbeatTimer = setInterval(() => {
            if (this.isConnected) {
                this.send({ type: 'ping' });
            }
        }, this.options.heartbeatInterval);
    }

    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    scheduleReconnect() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }

        this.reconnectAttempts++;
        const delay = Math.min(
            this.options.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
            30000 // Max 30 seconds
        );


        this.reconnectTimer = setTimeout(() => {
            this.connect();
        }, delay);
    }

    disconnect() {
        this.stopHeartbeat();
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }
        
        if (this.ws) {
            this.ws.close(1000, 'Client disconnect');
        }
    }

    // Event system
    on(event, handler) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event).push(handler);
    }

    off(event, handler) {
        if (this.eventHandlers.has(event)) {
            const handlers = this.eventHandlers.get(event);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    emit(event, data) {
        if (this.eventHandlers.has(event)) {
            this.eventHandlers.get(event).forEach(handler => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(\`Error in event handler for \${event}:\`, error);
                }
            });
        }
    }
}

// Usage Example
const client = new WebSocketClient('ws://localhost:8080');

// Set up event handlers
client.on('connected', () => {
    client.joinRoom('general');
});

client.on('chatMessage', (message) => {
});

client.on('roomJoined', (data) => {
});

client.on('error', (error) => {
    console.error('Client error:', error);
});

// Connect
client.connect();

// Send messages
setTimeout(() => {
    client.sendChatMessage('general', 'Hello, everyone!');
}, 2000);`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Long Polling Overview</h2>
          
          <h3 style={styles.heading}>What is Long Polling?</h3>
          <p style={styles.paragraph}>
            Long Polling is a technique where the client makes an HTTP request to the server, and the server 
            holds the request open until new data is available or a timeout occurs. This creates the illusion 
            of real-time communication while using standard HTTP infrastructure.
          </p>
          <pre style={styles.codeBlock}>
{`Long Polling Flow:

1. Client sends HTTP request to server
2. Server holds request open (doesn't respond immediately)
3. Server waits for new data or timeout
4. When data available or timeout:
   - Server responds with data (or empty response)
   - Connection closes
5. Client immediately sends new request (repeat)

Benefits:
✅ Works with existing HTTP infrastructure
✅ Firewall and proxy friendly
✅ Simple to implement and debug
✅ Graceful degradation
✅ No connection state management

Challenges:
❌ Higher latency than WebSockets
❌ HTTP overhead for each request
❌ Server resource consumption
❌ Complex timeout handling
❌ Potential for request buildup`}
          </pre>

          <h3 style={styles.heading}>Long Polling Server Implementation</h3>
          <p style={styles.paragraph}>
            Long Polling servers need to efficiently manage many concurrent connections and implement proper 
            timeout handling to prevent resource exhaustion.
          </p>
          <pre style={styles.codeBlock}>
{`Long Polling Server Implementation (Node.js):

const express = require('express');
const cors = require('cors');

class LongPollingServer {
    constructor() {
        this.app = express();
        this.clients = new Map();
        this.rooms = new Map();
        this.messageQueue = new Map();
        this.setupMiddleware();
        this.setupRoutes();
    }

    setupMiddleware() {
        this.app.use(cors());
        this.app.use(express.json());
        
        // Request logging
        this.app.use((req, res, next) => {
            next();
        });
    }

    setupRoutes() {
        // Long polling endpoint
        this.app.get('/poll/:clientId', (req, res) => {
            this.handlePollRequest(req, res);
        });

        // Send message endpoint
        this.app.post('/send', (req, res) => {
            this.handleSendMessage(req, res);
        });

        // Join room endpoint
        this.app.post('/join/:clientId/:room', (req, res) => {
            this.handleJoinRoom(req, res);
        });

        // Leave room endpoint
        this.app.post('/leave/:clientId/:room', (req, res) => {
            this.handleLeaveRoom(req, res);
        });

        // Client registration
        this.app.post('/register', (req, res) => {
            const clientId = this.generateClientId();
            this.clients.set(clientId, {
                id: clientId,
                rooms: new Set(),
                lastSeen: Date.now()
            });
            res.json({ clientId });
        });

        // Health check
        this.app.get('/health', (req, res) => {
            res.json({
                status: 'healthy',
                clients: this.clients.size,
                rooms: this.rooms.size,
                timestamp: Date.now()
            });
        });
    }

    handlePollRequest(req, res) {
        const clientId = req.params.clientId;
        const timeout = parseInt(req.query.timeout) || 30000; // 30 seconds default
        
        if (!this.clients.has(clientId)) {
            return res.status(404).json({ error: 'Client not found' });
        }

        // Update last seen
        this.clients.get(clientId).lastSeen = Date.now();

        // Check for queued messages
        const messages = this.getQueuedMessages(clientId);
        if (messages.length > 0) {
            return res.json({ messages });
        }

        // Set up long polling
        const pollTimeout = setTimeout(() => {
            // Timeout reached, send empty response
            res.json({ messages: [] });
            this.cleanupPollRequest(clientId, req);
        }, timeout);

        // Store polling request
        req.pollTimeout = pollTimeout;
        req.clientId = clientId;
        
        if (!this.messageQueue.has(clientId)) {
            this.messageQueue.set(clientId, []);
        }

        // Store the response object for later use
        if (!this.clients.get(clientId).pendingResponse) {
            this.clients.get(clientId).pendingResponse = res;
        }

        // Handle client disconnect
        req.on('close', () => {
            this.cleanupPollRequest(clientId, req);
        });

        req.on('error', (error) => {
            console.error(\`Poll request error for \${clientId}:\`, error);
            this.cleanupPollRequest(clientId, req);
        });
    }

    handleSendMessage(req, res) {
        const { fromClientId, room, message, type = 'chat' } = req.body;

        if (!fromClientId || !room || !message) {
            return res.status(400).json({ error: 'Missing required fields' });
        }

        if (!this.clients.has(fromClientId)) {
            return res.status(404).json({ error: 'Client not found' });
        }

        if (!this.rooms.has(room)) {
            return res.status(404).json({ error: 'Room not found' });
        }

        // Create message
        const messageObj = {
            id: this.generateMessageId(),
            type,
            fromClientId,
            room,
            content: message,
            timestamp: Date.now()
        };

        // Send to all room members except sender
        const room_members = this.rooms.get(room);
        let deliveredCount = 0;

        for (const clientId of room_members) {
            if (clientId !== fromClientId) {
                this.queueMessage(clientId, messageObj);
                if (this.deliverQueuedMessages(clientId)) {
                    deliveredCount++;
                }
            }
        }

        res.json({
            messageId: messageObj.id,
            delivered: deliveredCount,
            timestamp: messageObj.timestamp
        });
    }

    handleJoinRoom(req, res) {
        const { clientId, room } = req.params;

        if (!this.clients.has(clientId)) {
            return res.status(404).json({ error: 'Client not found' });
        }

        // Create room if it doesn't exist
        if (!this.rooms.has(room)) {
            this.rooms.set(room, new Set());
        }

        // Add client to room
        this.rooms.get(room).add(clientId);
        this.clients.get(clientId).rooms.add(room);

        // Notify other room members
        const joinMessage = {
            id: this.generateMessageId(),
            type: 'user_joined',
            clientId,
            room,
            timestamp: Date.now()
        };

        for (const memberId of this.rooms.get(room)) {
            if (memberId !== clientId) {
                this.queueMessage(memberId, joinMessage);
                this.deliverQueuedMessages(memberId);
            }
        }

        res.json({
            room,
            memberCount: this.rooms.get(room).size
        });
    }

    handleLeaveRoom(req, res) {
        const { clientId, room } = req.params;

        if (!this.clients.has(clientId) || !this.rooms.has(room)) {
            return res.status(404).json({ error: 'Client or room not found' });
        }

        // Remove client from room
        this.rooms.get(room).delete(clientId);
        this.clients.get(clientId).rooms.delete(room);

        // Clean up empty rooms
        if (this.rooms.get(room).size === 0) {
            this.rooms.delete(room);
        }

        // Notify remaining room members
        if (this.rooms.has(room)) {
            const leaveMessage = {
                id: this.generateMessageId(),
                type: 'user_left',
                clientId,
                room,
                timestamp: Date.now()
            };

            for (const memberId of this.rooms.get(room)) {
                this.queueMessage(memberId, leaveMessage);
                this.deliverQueuedMessages(memberId);
            }
        }

        res.json({ room });
    }

    queueMessage(clientId, message) {
        if (!this.messageQueue.has(clientId)) {
            this.messageQueue.set(clientId, []);
        }
        this.messageQueue.get(clientId).push(message);
    }

    getQueuedMessages(clientId) {
        const messages = this.messageQueue.get(clientId) || [];
        this.messageQueue.set(clientId, []); // Clear queue
        return messages;
    }

    deliverQueuedMessages(clientId) {
        const client = this.clients.get(clientId);
        if (!client || !client.pendingResponse) {
            return false;
        }

        const messages = this.getQueuedMessages(clientId);
        if (messages.length === 0) {
            return false;
        }

        try {
            client.pendingResponse.json({ messages });
            this.cleanupPollRequest(clientId, { clientId });
            return true;
        } catch (error) {
            console.error(\`Error delivering messages to \${clientId}:\`, error);
            return false;
        }
    }

    cleanupPollRequest(clientId, req) {
        if (req.pollTimeout) {
            clearTimeout(req.pollTimeout);
        }

        const client = this.clients.get(clientId);
        if (client) {
            client.pendingResponse = null;
        }
    }

    generateClientId() {
        return 'client_' + Math.random().toString(36).substr(2, 9);
    }

    generateMessageId() {
        return 'msg_' + Math.random().toString(36).substr(2, 9);
    }

    // Cleanup inactive clients
    startCleanupTimer() {
        setInterval(() => {
            const now = Date.now();
            const inactiveThreshold = 5 * 60 * 1000; // 5 minutes

            for (const [clientId, client] of this.clients) {
                if (now - client.lastSeen > inactiveThreshold) {
                    
                    // Remove from all rooms
                    for (const room of client.rooms) {
                        this.rooms.get(room)?.delete(clientId);
                        if (this.rooms.get(room)?.size === 0) {
                            this.rooms.delete(room);
                        }
                    }
                    
                    // Remove client
                    this.clients.delete(clientId);
                    this.messageQueue.delete(clientId);
                }
            }
        }, 60000); // Check every minute
    }

    start(port = 3001) {
        this.startCleanupTimer();
        this.app.listen(port, () => {
        });
    }
}

// Start server
const server = new LongPollingServer();
server.start();`}
          </pre>

          <h3 style={styles.heading}>Long Polling Client Implementation</h3>
          <p style={styles.paragraph}>
            Long Polling clients need to continuously poll the server and handle various error conditions 
            while maintaining the illusion of real-time communication.
          </p>
          <pre style={styles.codeBlock}>
{`Long Polling Client Implementation:

class LongPollingClient {
    constructor(baseUrl, options = {}) {
        this.baseUrl = baseUrl.replace(/\/$/, '');
        this.options = {
            pollTimeout: 30000,
            reconnectDelay: 5000,
            maxReconnectAttempts: 10,
            ...options
        };
        
        this.clientId = null;
        this.isPolling = false;
        this.reconnectAttempts = 0;
        this.eventHandlers = new Map();
        this.abortController = null;
    }

    async connect() {
        try {
            // Register with server
            const response = await fetch(\`\${this.baseUrl}/register\`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                throw new Error(\`Registration failed: \${response.status}\`);
            }

            const data = await response.json();
            this.clientId = data.clientId;
            
            this.emit('connected', { clientId: this.clientId });
            
            // Start polling
            this.startPolling();
            
        } catch (error) {
            console.error('Connection error:', error);
            this.emit('error', error);
            this.scheduleReconnect();
        }
    }

    startPolling() {
        if (this.isPolling || !this.clientId) return;
        
        this.isPolling = true;
        this.poll();
    }

    async poll() {
        if (!this.isPolling || !this.clientId) return;

        try {
            this.abortController = new AbortController();
            
            const response = await fetch(
                \`\${this.baseUrl}/poll/\${this.clientId}?timeout=\${this.options.pollTimeout}\`,
                {
                    method: 'GET',
                    signal: this.abortController.signal,
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            if (!response.ok) {
                throw new Error(\`Poll failed: \${response.status}\`);
            }

            const data = await response.json();
            
            // Process received messages
            if (data.messages && data.messages.length > 0) {
                data.messages.forEach(message => {
                    this.handleMessage(message);
                });
            }

            // Reset reconnect attempts on successful poll
            this.reconnectAttempts = 0;

            // Continue polling
            if (this.isPolling) {
                setTimeout(() => this.poll(), 100); // Small delay before next poll
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                // Poll was cancelled
                return;
            }

            console.error('Poll error:', error);
            this.emit('error', error);

            if (this.isPolling) {
                this.scheduleReconnect();
            }
        }
    }

    handleMessage(message) {
        switch (message.type) {
            case 'chat':
                this.emit('chatMessage', message);
                break;
            
            case 'user_joined':
                this.emit('userJoined', message);
                break;
            
            case 'user_left':
                this.emit('userLeft', message);
                break;
            
            default:
                this.emit('message', message);
        }
    }

    async sendMessage(room, content) {
        if (!this.clientId) {
            throw new Error('Not connected');
        }

        try {
            const response = await fetch(\`\${this.baseUrl}/send\`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fromClientId: this.clientId,
                    room,
                    message: content
                })
            });

            if (!response.ok) {
                throw new Error(\`Send failed: \${response.status}\`);
            }

            return await response.json();
        } catch (error) {
            console.error('Send message error:', error);
            throw error;
        }
    }

    async joinRoom(room) {
        if (!this.clientId) {
            throw new Error('Not connected');
        }

        try {
            const response = await fetch(\`\${this.baseUrl}/join/\${this.clientId}/\${room}\`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                throw new Error(\`Join room failed: \${response.status}\`);
            }

            const data = await response.json();
            this.emit('roomJoined', { room, memberCount: data.memberCount });
            return data;
        } catch (error) {
            console.error('Join room error:', error);
            throw error;
        }
    }

    async leaveRoom(room) {
        if (!this.clientId) {
            throw new Error('Not connected');
        }

        try {
            const response = await fetch(\`\${this.baseUrl}/leave/\${this.clientId}/\${room}\`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                throw new Error(\`Leave room failed: \${response.status}\`);
            }

            const data = await response.json();
            this.emit('roomLeft', { room });
            return data;
        } catch (error) {
            console.error('Leave room error:', error);
            throw error;
        }
    }

    scheduleReconnect() {
        if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            this.emit('maxReconnectAttemptsReached');
            return;
        }

        this.isPolling = false;
        this.reconnectAttempts++;

        const delay = Math.min(
            this.options.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
            30000
        );


        setTimeout(() => {
            this.connect();
        }, delay);
    }

    disconnect() {
        this.isPolling = false;
        
        if (this.abortController) {
            this.abortController.abort();
        }
        
        this.clientId = null;
        this.emit('disconnected');
    }

    // Event system
    on(event, handler) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event).push(handler);
    }

    off(event, handler) {
        if (this.eventHandlers.has(event)) {
            const handlers = this.eventHandlers.get(event);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    emit(event, data) {
        if (this.eventHandlers.has(event)) {
            this.eventHandlers.get(event).forEach(handler => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(\`Error in event handler for \${event}:\`, error);
                }
            });
        }
    }
}

// Usage Example
const client = new LongPollingClient('http://localhost:3001');

// Set up event handlers
client.on('connected', (data) => {
    client.joinRoom('general');
});

client.on('chatMessage', (message) => {
});

client.on('roomJoined', (data) => {
});

client.on('error', (error) => {
    console.error('Client error:', error);
});

// Connect
client.connect();

// Send messages
setTimeout(() => {
    client.sendMessage('general', 'Hello from long polling!');
}, 3000);`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>WebSockets vs Long Polling Comparison</h2>
          
          <h3 style={styles.heading}>Performance Comparison</h3>
          <p style={styles.paragraph}>
            WebSockets and Long Polling have different performance characteristics that make them suitable 
            for different types of applications and network conditions.
          </p>
          <pre style={styles.codeBlock}>
{`Performance Comparison:

Latency:
WebSockets:
  - Initial handshake: ~100-200ms
  - Message latency: ~1-10ms
  - Bidirectional: Instant both ways
  
Long Polling:
  - Initial request: ~50-100ms
  - Message latency: ~50-500ms
  - Unidirectional: Client->Server requires new request

Throughput:
WebSockets:
  - High message throughput
  - Minimal per-message overhead
  - Frame header: 2-14 bytes
  - Can handle 1000+ messages/second easily

Long Polling:
  - Lower message throughput
  - HTTP overhead per message
  - HTTP headers: 200-800 bytes
  - Typically 10-100 messages/second

Resource Usage:
WebSockets:
  - Persistent connections
  - Memory per connection: ~8KB
  - CPU: Low after connection established
  - Network: Minimal overhead

Long Polling:
  - Held HTTP connections
  - Memory per connection: ~16KB
  - CPU: Higher due to HTTP processing
  - Network: Higher overhead

Scalability:
WebSockets:
  - C10K problem: Can handle 10,000+ connections
  - Memory usage scales linearly
  - Requires sticky sessions for load balancing

Long Polling:
  - Limited by HTTP connection limits
  - Higher memory usage per connection
  - Easier to load balance (stateless)

Use Case Recommendations:

Choose WebSockets for:
✅ Real-time gaming
✅ Live collaboration (Google Docs style)
✅ High-frequency trading
✅ Live chat with high message volume
✅ Real-time dashboards with frequent updates
✅ Multiplayer applications
✅ Live streaming data

Choose Long Polling for:
✅ Simple notifications
✅ Occasional updates
✅ Behind corporate firewalls
✅ Simple infrastructure requirements
✅ Lower message frequency
✅ RESTful API integration
✅ When WebSocket support is uncertain`}
          </pre>

          <h3 style={styles.heading}>Infrastructure Considerations</h3>
          <p style={styles.paragraph}>
            The choice between WebSockets and Long Polling often depends on infrastructure constraints, 
            deployment environments, and operational requirements.
          </p>
          <pre style={styles.codeBlock}>
{`Infrastructure Considerations:

Load Balancing:
WebSockets:
  - Requires sticky sessions
  - Connection state tied to specific server
  - Load balancer must support WebSocket upgrade
  - Popular solutions: nginx, HAProxy, AWS ALB

Long Polling:
  - Stateless (easier to load balance)
  - Any server can handle any request
  - Standard HTTP load balancing works
  - No special configuration needed

Proxies and Firewalls:
WebSockets:
  - May be blocked by corporate firewalls
  - Some proxies don't support WebSocket upgrade
  - Port 80/443 usually works
  - May need fallback to Long Polling

Long Polling:
  - Works with all HTTP proxies
  - Firewall friendly (standard HTTP)
  - No special configuration needed
  - Universal compatibility

Monitoring and Debugging:
WebSockets:
  - Custom monitoring tools needed
  - Binary protocol (harder to debug)
  - Connection state tracking required
  - Specialized logging needed

Long Polling:
  - Standard HTTP monitoring works
  - Easy to debug with curl/Postman
  - Standard web server logs
  - Familiar debugging tools

Deployment Complexity:
WebSockets:
  - Requires WebSocket-aware infrastructure
  - Connection state management
  - Graceful shutdown complexity
  - Health check challenges

Long Polling:
  - Standard HTTP deployment
  - Stateless server design
  - Simple health checks
  - Easy horizontal scaling

CDN and Caching:
WebSockets:
  - CDNs generally don't cache WebSocket connections
  - Direct server connections required
  - Geographic distribution more complex

Long Polling:
  - CDNs can optimize initial connections
  - Static assets can be cached
  - Better geographic distribution support

Example Load Balancer Configuration:

# nginx WebSocket configuration
upstream websocket_backend {
    ip_hash;  # Sticky sessions
    server backend1.example.com:8080;
    server backend2.example.com:8080;
}

server {
    listen 80;
    location /ws/ {
        proxy_pass http://websocket_backend;
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection "upgrade";
        proxy_set_header Host $host;
        proxy_read_timeout 86400;
    }
}

# nginx Long Polling configuration
upstream longpoll_backend {
    server backend1.example.com:3001;
    server backend2.example.com:3001;
    server backend3.example.com:3001;
}

server {
    listen 80;
    location /poll/ {
        proxy_pass http://longpoll_backend;
        proxy_read_timeout 35s;  # Slightly longer than poll timeout
        proxy_send_timeout 35s;
    }
}`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Hybrid Approaches</h2>
          
          <h3 style={styles.heading}>WebSocket with Long Polling Fallback</h3>
          <p style={styles.paragraph}>
            Many production systems implement hybrid approaches that attempt WebSocket connections first 
            and fall back to Long Polling when WebSockets are not available or fail to connect.
          </p>
          <pre style={styles.codeBlock}>
{`Hybrid Implementation:

class HybridRealTimeClient {
    constructor(options = {}) {
        this.options = {
            websocketUrl: options.websocketUrl,
            longPollUrl: options.longPollUrl,
            preferWebSocket: true,
            fallbackDelay: 3000,
            ...options
        };
        
        this.currentTransport = null;
        this.transportType = null;
        this.eventHandlers = new Map();
        this.isConnected = false;
    }

    async connect() {
        if (this.options.preferWebSocket && this.options.websocketUrl) {
            try {
                await this.connectWebSocket();
                return;
            } catch (error) {
            }
        }
        
        if (this.options.longPollUrl) {
            await this.connectLongPolling();
        } else {
            throw new Error('No transport method available');
        }
    }

    async connectWebSocket() {
        return new Promise((resolve, reject) => {
            const ws = new WebSocketClient(this.options.websocketUrl);
            
            const connectTimeout = setTimeout(() => {
                ws.disconnect();
                reject(new Error('WebSocket connection timeout'));
            }, this.options.fallbackDelay);

            ws.on('connected', () => {
                clearTimeout(connectTimeout);
                this.currentTransport = ws;
                this.transportType = 'websocket';
                this.isConnected = true;
                this.setupTransportHandlers();
                resolve();
            });

            ws.on('error', (error) => {
                clearTimeout(connectTimeout);
                reject(error);
            });

            ws.connect();
        });
    }

    async connectLongPolling() {
        const lp = new LongPollingClient(this.options.longPollUrl);
        
        return new Promise((resolve, reject) => {
            lp.on('connected', () => {
                this.currentTransport = lp;
                this.transportType = 'longpolling';
                this.isConnected = true;
                this.setupTransportHandlers();
                resolve();
            });

            lp.on('error', (error) => {
                reject(error);
            });

            lp.connect();
        });
    }

    setupTransportHandlers() {
        // Forward all events from transport to hybrid client
        const eventTypes = ['chatMessage', 'userJoined', 'userLeft', 'roomJoined', 'error', 'disconnected'];
        
        eventTypes.forEach(eventType => {
            this.currentTransport.on(eventType, (data) => {
                this.emit(eventType, { ...data, transport: this.transportType });
            });
        });

        // Handle disconnections with potential transport switching
        this.currentTransport.on('disconnected', () => {
            this.isConnected = false;
            this.handleDisconnection();
        });
    }

    async handleDisconnection() {
        
        // Try to reconnect with same transport first
        try {
            if (this.transportType === 'websocket') {
                await this.connectWebSocket();
                return;
            } else {
                await this.connectLongPolling();
                return;
            }
        } catch (error) {
        }

        // Try alternative transport
        try {
            if (this.transportType === 'websocket' && this.options.longPollUrl) {
                await this.connectLongPolling();
            } else if (this.transportType === 'longpolling' && this.options.websocketUrl) {
                await this.connectWebSocket();
            }
        } catch (error) {
            console.error('All transport methods failed:', error);
            this.emit('connectionFailed', error);
        }
    }

    // Proxy methods to current transport
    sendMessage(room, content) {
        if (!this.isConnected || !this.currentTransport) {
            throw new Error('Not connected');
        }
        return this.currentTransport.sendMessage(room, content);
    }

    joinRoom(room) {
        if (!this.isConnected || !this.currentTransport) {
            throw new Error('Not connected');
        }
        return this.currentTransport.joinRoom(room);
    }

    leaveRoom(room) {
        if (!this.isConnected || !this.currentTransport) {
            throw new Error('Not connected');
        }
        return this.currentTransport.leaveRoom(room);
    }

    disconnect() {
        if (this.currentTransport) {
            this.currentTransport.disconnect();
            this.currentTransport = null;
            this.transportType = null;
            this.isConnected = false;
        }
    }

    getTransportType() {
        return this.transportType;
    }

    // Event system
    on(event, handler) {
        if (!this.eventHandlers.has(event)) {
            this.eventHandlers.set(event, []);
        }
        this.eventHandlers.get(event).push(handler);
    }

    emit(event, data) {
        if (this.eventHandlers.has(event)) {
            this.eventHandlers.get(event).forEach(handler => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(\`Error in event handler for \${event}:\`, error);
                }
            });
        }
    }
}

// Usage
const client = new HybridRealTimeClient({
    websocketUrl: 'ws://localhost:8080',
    longPollUrl: 'http://localhost:3001',
    preferWebSocket: true
});

client.on('chatMessage', (data) => {
});

client.connect();`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose the right technology:</strong> Use WebSockets for high-frequency, low-latency 
              communication and Long Polling for simpler, less frequent updates.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper error handling:</strong> Handle network failures, timeouts, and 
              connection drops gracefully with automatic reconnection logic.
            </li>
            <li style={styles.listItem}>
              <strong>Use heartbeats and health checks:</strong> Implement ping/pong mechanisms to detect 
              dead connections and maintain connection health.
            </li>
            <li style={styles.listItem}>
              <strong>Consider hybrid approaches:</strong> Implement fallback mechanisms to ensure 
              compatibility across different network environments.
            </li>
            <li style={styles.listItem}>
              <strong>Optimize for your infrastructure:</strong> Consider load balancing, proxy support, 
              and deployment complexity when choosing your approach.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor performance:</strong> Track connection counts, message latency, and resource 
              usage to optimize your real-time communication system.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            WebSockets and Long Polling are both valuable techniques for real-time communication, each with 
            distinct advantages. WebSockets excel in high-performance scenarios with frequent bidirectional 
            communication, while Long Polling provides a simpler, more compatible solution for less demanding 
            real-time requirements.
          </p>
          <p style={styles.paragraph}>
            The choice between them should be based on your specific requirements for latency, throughput, 
            infrastructure constraints, and operational complexity. Many successful applications use hybrid 
            approaches to get the best of both worlds.
          </p>
        </section>
      </main>
    </article>
  );
};

export default WebSocketsLongPolling;
